import { useState } from "react";
import { LifeBuoy, Bug, CheckCircle2, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/api/client";
import { toast } from "sonner";

export function SupportCenter({ open, onOpenChange }) {
  const [requestType, setRequestType] = useState("question");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const reset = () => {
    setRequestType("question");
    setDescription("");
    setSent(false);
  };

  const handleOpenChange = (next) => {
    onOpenChange(next);
    if (!next) setTimeout(reset, 200);
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setLoading(true);
    try {
      await api.post("/support/request", { request_type: requestType, description: description.trim() });
      setSent(true);
    } catch (e) {
      toast.error("Could not send your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="p-0 w-full sm:max-w-lg flex flex-col gap-0" data-testid="support-center-panel">
        <SheetHeader className="px-5 py-4 border-b border-slate-200 space-y-0 text-left">
          <SheetTitle className="text-base font-semibold text-slate-900">Contact Support</SheetTitle>
          <SheetDescription className="sr-only">Send a support question or bug report to the ModernTect team</SheetDescription>
        </SheetHeader>
        <div className="p-5 flex-1 overflow-y-auto">
          {sent ? (
            <div className="text-center py-6" data-testid="support-request-sent-panel">
              <CheckCircle2 className="text-emerald-500 mx-auto mb-3" size={32} />
              <h3 className="font-semibold text-slate-900 mb-1">Thanks, we got it</h3>
              <p className="text-sm text-slate-500 mb-4">The ModernTect team will follow up with you by email if needed.</p>
              <Button onClick={() => handleOpenChange(false)} data-testid="support-request-done-button" className="bg-blue-600 hover:bg-blue-700">
                Done
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">What's this about?</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="support-type-question-button"
                    onClick={() => setRequestType("question")}
                    className={`flex items-center gap-2 rounded-lg border-2 p-3 text-left transition-colors ${
                      requestType === "question" ? "border-blue-500 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <LifeBuoy size={16} className={requestType === "question" ? "text-blue-600" : "text-slate-400"} />
                    <span className="text-sm font-medium text-slate-800">Support Question</span>
                  </button>
                  <button
                    type="button"
                    data-testid="support-type-bug-button"
                    onClick={() => setRequestType("bug")}
                    className={`flex items-center gap-2 rounded-lg border-2 p-3 text-left transition-colors ${
                      requestType === "bug" ? "border-blue-500 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <Bug size={16} className={requestType === "bug" ? "text-blue-600" : "text-slate-400"} />
                    <span className="text-sm font-medium text-slate-800">Bug Report</span>
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">
                  {requestType === "bug" ? "What went wrong?" : "What's your question?"}
                </p>
                <Textarea
                  data-testid="support-description-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={requestType === "bug" ? "Tell us what happened and what you expected instead..." : "Ask us anything..."}
                  className="min-h-[140px] resize-none"
                />
              </div>
              <p className="text-xs text-slate-400">We'll include your name and email automatically so we can follow up.</p>
              <Button
                onClick={handleSubmit}
                disabled={loading || !description.trim()}
                data-testid="support-submit-button"
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                Submit
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
